import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { Spin, Empty, message } from 'antd';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';
import style from './index.module.less';
import xlsxSvg from './assets/xlsx.svg';
import zipSvg from './assets/zip.svg';
import pdfSvg from './assets/pdf.svg';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@/hooks/useTranslation';
import { getArticleDetail, getPreviewFile, type FileInfoVo } from '@/api/sip/notification';
import { report } from '@/utils/report';
import { formatNoticeTime } from '@/utils/formatTime';

const NOTICE_DETAIL_PAGE_ID = 'P20393';
const NOTICE_DETAIL_VIEW_BUTTON_ID = 'B001';

function getFileTypeSvg(fileName: string) {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'xlsx':
    case 'xls':
      return xlsxSvg;
    case 'zip':
      return zipSvg;
    case 'pdf':
      return pdfSvg;
    default:
      return '';
  }
}

export default () => {
  const $ = useTranslation();
  const { noticeId } = useParams<{ noticeId: string }>();
  const countryCode = useSelector<RootState, string>(
    state => state.LocaleModel.countryCode,
    shallowEqual
  );
  const userId = useSelector<RootState, string | number>(
    state => state.UserModel.currentUser?.uid ?? '',
    shallowEqual
  );

  const { data: detail, loading } = useRequest(
    async () => {
      if (!noticeId) return null;
      const res = await getArticleDetail(noticeId);
      if (res.code !== 200) return null;
      return res.data ?? null;
    },
    { refreshDeps: [noticeId] }
  );

  useEffect(() => {
    if (!detail) return;
    report({
      data: {
        pageId: NOTICE_DETAIL_PAGE_ID,
        buttonId: NOTICE_DETAIL_VIEW_BUTTON_ID,
        countryOrRegion: countryCode,
        uuid: detail.articleUuid,
        text_lang: detail.lang,
        userId
      }
    });
  }, [detail, countryCode, userId]);

  /**
   * 附件下载 —— 通过 base 通用下载接口 `/api/service/v1/file/operate/download`
   * 拿到 OSS 签名 URL 后再 window.open，避免直接 open objectKey 打开空白页。
   * `language` 请求头由 `@/api/ajax` 全局自动注入。
   */
  const handleDownload = async (file: FileInfoVo) => {
    if (!file.fileId) {
      message.error($('下载失败'));
      return;
    }
    const res = await getPreviewFile({ fileId: file.fileId });
    if (res.code !== 200) {
      message.error(res.msg);
      return;
    }
    const url = res.data?.downloadUrl;
    if (!url) {
      message.error($('下载失败'));
      return;
    }
    window.open(url, '_blank');
  };

  return (
    <div className={style.container}>
      <Spin spinning={loading}>
        {detail ? (
          <>
            <div className={style.title}>{detail.title}</div>
            <div className={style.time}>{formatNoticeTime(detail.publishTime, countryCode)}</div>
            <div className={style.line} />
            {detail.summary && <div className={style.summary}>{detail.summary}</div>}
            <div
              className={style.content}
              dangerouslySetInnerHTML={{ __html: detail.textContent ?? '' }}
            />
            {detail.fileList && detail.fileList.length > 0 && (
              <>
                <div className={style.subtitle}>{$('附件')}</div>
                <div className={style.assets}>
                  {detail.fileList.map((item: FileInfoVo) => (
                    <div key={item.fileId} className={style['assets-item']}>
                      <span className={style['assets-item-bd']}>
                        <img
                          src={getFileTypeSvg(item.fileName)}
                          alt={item.fileName}
                          className={style['assets-item-icon']}
                        />
                        <span
                          className={style['assets-item-name']}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleDownload(item)}
                        >
                          {item.fileName}
                        </span>
                        <DownloadOutlined
                          style={{ fontSize: '16px', color: '#0068FF', cursor: 'pointer' }}
                          onClick={() => handleDownload(item)}
                        />
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          !loading && <Empty description={$('暂无数据')} />
        )}
      </Spin>
    </div>
  );
};
